import { useMemo, useSyncExternalStore, type ComponentPropsWithRef, type MouseEvent } from "react";

import { parsePath, pathOf, type Route } from "./routes";

/**
 * A deliberately small router: the History API plus typed routes. Pages are chosen in AppRoutes by route name.
 * Links are real <a href> elements, so open-in-new-tab, copy-link and middle-click all work.
 */

const NAVIGATE_EVENT = "orthos:navigate";

function subscribe(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  window.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

const currentPath = () => window.location.pathname;
const currentSearch = () => window.location.search;

export function navigate(to: Route | string, options: { replace?: boolean } = {}): void {
  const href = typeof to === "string" ? to : pathOf(to);
  if (href === window.location.pathname + window.location.search) return;
  if (options.replace === true) window.history.replaceState(null, "", href);
  else window.history.pushState(null, "", href);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

export function useRoute(): Route {
  const pathname = useSyncExternalStore(subscribe, currentPath);
  return useMemo(() => parsePath(pathname), [pathname]);
}

/** Query parameters of the current URL, e.g. useSearchParams().get("sel") (FR-PROJ-059). */
export function useSearchParams(): URLSearchParams {
  const search = useSyncExternalStore(subscribe, currentSearch);
  return useMemo(() => new URLSearchParams(search), [search]);
}

/** Replace one query parameter without adding a history entry (selection changes, filters). */
export function setSearchParam(name: string, value: string | undefined): void {
  const params = new URLSearchParams(window.location.search);
  if (value === undefined || value === "") params.delete(name);
  else params.set(name, value);
  const query = params.toString();
  navigate(`${window.location.pathname}${query === "" ? "" : `?${query}`}`, { replace: true });
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export interface LinkProps extends Omit<ComponentPropsWithRef<"a">, "href"> {
  to: Route;
  /** Mark the link as the current page (sets aria-current="page"). */
  current?: boolean;
}

export function Link({ to, current = false, onClick, target, ...props }: LinkProps) {
  return (
    <a
      href={pathOf(to)}
      target={target}
      aria-current={current ? "page" : undefined}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          !isPlainLeftClick(event) ||
          (target !== undefined && target !== "_self")
        )
          return;
        event.preventDefault();
        navigate(to);
      }}
      {...props}
    />
  );
}
